import { callAPI } from "@/api/httpservice";
import { useFileStore } from "@/stores/fileStore";
import type {
  Athlete,
  EnvelopeResponse,
  Enveloppe,
  File,
  FolderCount,
} from "@/types/file.types";
import { serverUrls } from "@/utils/constants";
import { getAuthToken } from "./auth.service";

export function editAthleteInfo(
  editableValues: {
    countryCode: string;
    firstName: string;
    lastName: string;
  },
  athleteId: string,
  oldDisciplineCode: string
) {
  return callAPI<Athlete>("PATCH", `/athletes/${athleteId}/${oldDisciplineCode}`, editableValues);
}

export function getFileName(filePath: string) {
  const path = filePath;
  const cleanPath = path.includes("?") ? path.split("?")[0] : path;
  const fileName = cleanPath.substring(cleanPath.lastIndexOf("/") + 1);
  return fileName;
}

export function getFileType(filePath: string) {
  const path = filePath;
  const cleanPath = path.includes("?") ? path.split("?")[0] : path;
  const extension = cleanPath
    .substring(cleanPath.lastIndexOf(".") + 1)
    .toUpperCase();
  return extension;
}

export function getReplacedFileType(filePath: string) {
  const regex = /^data:([a-zA-Z0-9]+\/[a-zA-Z0-9-+]+);base64,/;
  const match = regex.exec(filePath);

  if (match) {
    const mimeType = match[1] as keyof typeof mimeTypeMapping;

    const mimeTypeMapping = {
      "image/jpeg": "jpg",
      "image/png": "png",
      "audio/wav": "wav",
      "audio/mp3": "mp3",
    };

    return mimeTypeMapping[mimeType] || "";
  }

  return "";
}

export async function downloadFile(filePath: string) {
  const file = await fetch(filePath);
  const fileBlob = await file.blob();
  const fileUrl = URL.createObjectURL(fileBlob);
  const a = document.createElement("a");
  a.href = fileUrl;
  a.download = getFileName(filePath) || "download";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

export function getImageFileInfo(
  filePath: string
): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = filePath;
    img.onload = () => {
      resolve({ width: img.width, height: img.height });
    };
    img.onerror = () => {
      reject({ width: 0, height: 0 });
    };
  });
}

export function getAudioFileInfo(filePath: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const audio = new Audio();
    audio.src = filePath;
    audio.onloadedmetadata = () => {
      const duration = `${Math.floor(audio.duration / 60)}:${String(
        Math.floor(audio.duration % 60)
      ).padStart(2, "0")}`;
      resolve(duration);
    };
    audio.onerror = () => {
      reject("00:00");
    };
  });
}

export function splitFileInfo(fileInfo: string) {
  const parts = fileInfo.split(":");
  return {
    step: parts[0] || null,
    category: parts[1] || null,
    name: parts[2] || null,
    bgType: parts[3] || null,
  };
}


export function getFolderById(id: string) {
  return callAPI<EnvelopeResponse>("GET", `/envelopes/${id}`, null);
}

export function getFolderForMoveList(
  foldersList: string,
  limit: number,
  page: number,
  search: string,
  sortOrder: string,
  dateFilter: string
) {
  const params = new URLSearchParams({
    status: foldersList,
    limit: String(limit),
    page: String(page),
    search,
    getEmpty: "yes",
  });
  if (sortOrder) params.append("orderBy", sortOrder);
  if (dateFilter) params.append("timeAgo", dateFilter);

  return callAPI<{ total: number; items: Enveloppe[] }>(
    "GET",
    `/envelopes?${params.toString()}`,
    null
  );
}

export function getFoldersList(
  foldersList: string,
  limit: number,
  page: number,
  search: string,
  sortOrder: string,
  dateFilter: string,
  showFolderNoFiles: boolean,
  clusterId: string,
  siteId: string,
  kitId: string,
  countryCode?: string,
  disciplineCode?: string,
  processType?: string,
  status?: string
) {
  const params = new URLSearchParams({
    status: foldersList,
    limit: String(limit),
    page: String(page),
    search,
    getEmpty: showFolderNoFiles ? "yes" : "no",
  });
  if (sortOrder) params.append("orderBy", sortOrder);
  if (dateFilter) params.append("timeAgo", dateFilter);
  if (countryCode) params.append("countryCode", countryCode);
  if (disciplineCode) params.append("disciplineCode", disciplineCode);
  if (processType) params.append("processType", processType);
  if (clusterId) params.append("clusterId", clusterId);
  if (siteId) params.append("siteId", siteId);
  if (kitId) params.append("kitId", kitId);
  if (status) params.append("lifecycleStatus", status);

  return callAPI<{ total: number; items: Enveloppe[] }>(
    "GET",
    `/envelopes?${params.toString()}`,
    null
  );
}


export function getFilesList(
  filesList: string,
  limit: number,
  page: number,
  search: string,
  sortOrder: string,
  dateFilter: string,
  clusterId: string,
  siteId: string,
  kitId: string,
  moment?: string,
  processType?:string
) {
  const params = new URLSearchParams({
    limit: String(limit),
    page: String(page),
    search
  });

  if (sortOrder) params.append("orderBy", sortOrder);
  if (dateFilter) params.append("timeAgo", dateFilter);
  if (filesList) params.append("status", filesList);
  if (moment) params.append("moment", moment);
  if (clusterId) params.append("clusterId", clusterId);
  if (siteId) params.append("siteId", siteId);
  if (kitId) params.append("kitId", kitId);
  if (processType) params.append("processType", processType);

  return callAPI<{ total: number; items: File[] }>(
    "GET",
    `/files?${params.toString()}`,
    null
  );
}


export function getFolderCount() {
  return callAPI<FolderCount>("GET", `/envelopes/counts`, null);
}

export function moveFile(fileId: string, envelopeId: string) {
  return callAPI<void>(
    "PATCH",
    `/files/${fileId}/envelopes/${envelopeId}`,
    null
  );
}

export function moveFileToUnrecognized(fileId: string) {
  return callAPI<void>("PATCH", `/files/${fileId}/unlink`, null);
}

export function moveFileToDiscarded(fileId: string) {
  return callAPI<void>("PATCH", `/files/${fileId}/discarded`, null);
}

export function regenFileFromFolder(envelopeId: string) {
  return callAPI<void>("PATCH", `/envelopes/${envelopeId}/regenerate`, null);
}

export function moveEnveloppeOutOfProd(envelopeId: string) {
  return callAPI<void>("PATCH", `/envelopes/${envelopeId}/move-out`, null);
}

export function sendEnvelopeToValidation(folderId: string) {
  return callAPI<Enveloppe>("PATCH", `/envelopes/${folderId}/validate`, null);
}

export function sendEnvelopeToQueue(folderId: string) {
  return callAPI<Enveloppe>("PATCH", `/envelopes/${folderId}/queue-validation`, null);
}

export function processBackup(envelopeId: string, fileId: string) {
  return callAPI<void>(
    "POST",
    `/envelopes/${envelopeId}/backup/files/${fileId}`,
    null
  );
}

export function replaceDuplicatedFile(fileId: string, replaceFileId: string) {
  return callAPI<void>("PATCH", `/envelopes/swap-files`, {
    mainEnvelopeFileId: fileId,
    duplicateEnvelopeFileId: replaceFileId,
  });
}

export function sendFileToReco(fileId: string) {
  return callAPI<void>("PATCH", `/files/${fileId}/recognition`, null);
}


export function forceSendValidation(envelopeId: string) {
  return callAPI<Enveloppe>("PATCH", `/envelopes/${envelopeId}/force-validation-status`, null);
}

export function removeFromQueues(envelopeId: string) {
  return callAPI<Enveloppe>("PATCH", `/envelopes/${envelopeId}/remove-queues`, null);
}

export function sendAllFileToReco() {
  return callAPI<void>("PATCH", `/files/all-files-recognition`, null);
}

export function uploadInitialFileToFolder(
  file: any,
  envelopeId: string,
  description: string
) {
  return new Promise((resolve, reject) => {
    const fileStore = useFileStore();
    const token = getAuthToken();
    if (!token) {
      return false;
    }
    if (token) {
      const xhr = new XMLHttpRequest();
      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve({ status: xhr.status, body: xhr.responseText });
        } else {
          reject(new Error("Request failed with status: " + xhr.status));
        }
      });
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          fileStore.progress = Math.round((event.loaded / event.total) * 100);
        }
      };
      xhr.addEventListener("error", () =>
        reject(new Error("File upload failed"))
      );
      xhr.addEventListener("abort", () =>
        reject(new Error("File upload aborted"))
      );
      xhr.open(
        "POST",
        `${serverUrls.authUrl + "/envelopes/" + envelopeId + "/files"}`,
        true
      );
      xhr.setRequestHeader("Authorization", "Bearer " + token);
      xhr.setRequestHeader("Accept", "application/json");
      xhr.setRequestHeader("Access-Control-Allow-Origin", "*");
      const formData = new FormData();
      formData.append("file", file);
      formData.append("description", description);

      xhr.send(formData);
    }
  });
}

export function uploadFileForBackup(file: any, envelopeId: string) {
  return new Promise((resolve, reject) => {
    const fileStore = useFileStore();
    const token = getAuthToken();
    if (!token) {
      return false;
    }
    if (token) {
      const xhr = new XMLHttpRequest();
      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve({ status: xhr.status, body: xhr.responseText });
        } else {
          reject(new Error("Request failed with status: " + xhr.status));
        }
      });
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          fileStore.progress = Math.round((event.loaded / event.total) * 100);
        }
      };
      xhr.addEventListener("error", () =>
        reject(new Error("File upload failed"))
      );
      xhr.addEventListener("abort", () =>
        reject(new Error("File upload aborted"))
      );
      xhr.open(
        "POST",
        `${serverUrls.authUrl + "/envelopes/" + envelopeId + "/backup/files"}`,
        true
      );
      xhr.setRequestHeader("Authorization", "Bearer " + token);
      xhr.setRequestHeader("Accept", "application/json");
      xhr.setRequestHeader("Access-Control-Allow-Origin", "*");
      const formData = new FormData();
      formData.append("file", file);
      xhr.send(formData);
    }
  });
}

export function uploadReferenceFile(files: any) {
  return new Promise((resolve, reject) => {
    const fileStore = useFileStore();
    const token = getAuthToken();
    if (!token) {
      return false;
    }
    if (token) {
      const xhr = new XMLHttpRequest();
      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(JSON.parse(xhr.response));
        } else {
          reject(new Error("Request failed with status: " + xhr.status));
        }
      });
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          fileStore.progress = Math.round((event.loaded / event.total) * 100);
        }
      };
      xhr.addEventListener("error", () =>
        reject(new Error("File upload failed"))
      );
      xhr.addEventListener("abort", () =>
        reject(new Error("File upload aborted"))
      );
      xhr.open("POST", `${serverUrls.authUrl + "/athletes"}`, true);
      xhr.setRequestHeader("Authorization", "Bearer " + token);
      xhr.setRequestHeader("Accept", "application/json");
      xhr.setRequestHeader("Access-Control-Allow-Origin", "*");
      const formData = new FormData();
      files.forEach((file: string | Blob) => {
        formData.append("files", file);
      });
      xhr.send(formData);
    }
  });
}

export function uploadAthleteFile(files: any,mode: "classic" | "backup") {
  return new Promise((resolve, reject) => {
    const fileStore = useFileStore();
    const token = getAuthToken();
    if (!token) {
      return false;
    }
    if (token) {
      const xhr = new XMLHttpRequest();
      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(JSON.parse(xhr.response));
        } else {
          reject(new Error("Request failed with status: " + xhr.status));
        }
      });
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          fileStore.progress = Math.round((event.loaded / event.total) * 100);
        }
      };
      xhr.addEventListener("error", () =>
        reject(new Error("File upload failed"))
      );
      xhr.addEventListener("abort", () =>
        reject(new Error("File upload aborted"))
      );
      xhr.open("POST", `${serverUrls.authUrl + `/files/upload?processType=${mode}`}`, true);
      xhr.setRequestHeader("Authorization", "Bearer " + token);
      xhr.setRequestHeader("Accept", "application/json");
      xhr.setRequestHeader("Access-Control-Allow-Origin", "*");
      const formData = new FormData();
      files.forEach((file: string | Blob) => {
        formData.append("files", file);
      });
      xhr.send(formData);
    }
  });
}